import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, Share2, CalendarPlus, Loader2, Ticket, X, CreditCard, Tag } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../lib/data";
import { PageHeader } from "../components/PageHeader";
import { Btn, Field } from "../components/ui-kit";
import { Reveal } from "../lib/motion";
import { useContent, resolveImg, api } from "../lib/api";
import { useMember } from "../lib/memberAuth";

const fmt = (d) => {
  if (!d) return "";
  const s = String(d);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

function gcalLink(e) {
  const start = e.date.replace(/-/g, "");
  const title = encodeURIComponent(e.title);
  const details = encodeURIComponent(e.desc);
  const loc = encodeURIComponent(e.location);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${start}&details=${details}&location=${loc}`;
}

const tierOpen = (t) => {
  const n = new Date().toISOString().slice(0, 10);
  if (t.sales_start && n < t.sales_start) return false;
  if (t.sales_end && n > t.sales_end) return false;
  if (t.capacity !== "" && t.capacity !== null && t.capacity !== undefined && Number(t.sold || 0) >= Number(t.capacity)) return false;
  return true;
};

export default function Events() {
  const { data: EVENTS, loading } = useContent("events");
  const { member } = useMember();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [tform, setTform] = useState({ name: "", email: "", phone: "", quantity: 1 });
  const [tierId, setTierId] = useState(null);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [buying, setBuying] = useState(false);
  const timer = useRef(null);

  const tiers = (ev?.ticket_tiers || []).filter((t) => t.name);
  const hasTiers = tiers.length > 0;

  const openTickets = (e) => {
    setTform({
      name: member ? (member.preferred_name || member.name || "") : "",
      email: member ? member.email || "" : "",
      phone: member ? member.phone || "" : "", quantity: 1,
    });
    setPromo(""); setAppliedPromo(""); setQuote(null);
    const ts = (e.ticket_tiers || []).filter((t) => t.name);
    setTierId(ts.length > 0 ? (ts.find(tierOpen)?.id || ts[0].id) : null);
    setEv(e);
  };

  useEffect(() => {
    if (!ev) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/checkout/quote", {
          cart: [{ type: "ticket", id: ev.id, tier_id: tierId, qty: Number(tform.quantity) || 1 }],
          promo_code: appliedPromo, email: tform.email || "",
        });
        setQuote(data);
        if (appliedPromo && data.promo_error) toast.error(data.promo_error);
      } catch (e) {
        setQuote({ error: e?.response?.data?.detail || "Unable to price tickets" });
      }
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [ev, tierId, tform.quantity, appliedPromo]);

  const buyTickets = async (method) => {
    if (!tform.name || !tform.email) { toast.error("Please enter your name and email."); return; }
    setBuying(method);
    try {
      const { data } = await api.post("/checkout/create", {
        cart: [{ type: "ticket", id: ev.id, tier_id: tierId, qty: Number(tform.quantity) || 1 }],
        promo_code: appliedPromo, name: tform.name, email: tform.email, phone: tform.phone,
        method, origin_url: window.location.origin,
      });
      if (data.free) { navigate(`/order/success?order_id=${data.order_id}`); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start checkout. Please try again.");
      setBuying(false);
    }
  };

  const share = async (e) => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: e.title, url }); } catch (err) { console.debug("Share dismissed", err); } }
    else { navigator.clipboard.writeText(url); toast.success("Event link copied!"); }
  };

  const isSoldOut = (e) => e.sold_out || (e.tickets_left !== undefined && e.tickets_left !== null && e.tickets_left !== "" && Number(e.tickets_left) <= 0);
  const hasPaidTickets = (e) => Number(e.price) > 0 || (e.ticket_tiers || []).some((t) => Number(t.price) > 0);

  const row = (label, val, opts = {}) => (
    <div className={`flex justify-between text-sm ${opts.bold ? "font-bold text-base pt-2 border-t border-black/10" : "text-black/60"}`}>
      <span>{label}</span><span className={opts.red ? "text-[#E63946] font-medium" : ""}>{val}</span>
    </div>
  );

  return (
    <div data-testid="events-page">
      <PageHeader eyebrow="What's Happening" title={"Upcoming\nEvents"} sub="Worship services, revivals, outreach, youth nights, and more. Come be part of what God is doing at One Love." image={IMAGES.crowd} />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 space-y-6">
          {loading && <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" size={32} /></div>}
          {!loading && (EVENTS || []).length === 0 && <p className="text-center text-black/50 py-20">No upcoming events right now. Check back soon!</p>}
          {(EVENTS || []).map((e, i) => (
            <Reveal key={e.id || e.title} delay={(i % 2) * 0.08}>
              <div className="group grid md:grid-cols-[340px_1fr] gap-0 border border-black/10 rounded-sm overflow-hidden hover:border-[#E63946] transition-colors">
                <div className="overflow-hidden aspect-[16/10] md:aspect-auto bg-[#F4F4F5]">
                  {e.image ? <img src={resolveImg(e.image)} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : null}
                </div>
                <div className="p-7 sm:p-9 flex flex-col justify-center">
                  <span className="inline-block w-fit text-xs uppercase tracking-[0.2em] text-[#E63946] mb-3">{e.category}</span>
                  <h3 className="font-display font-bold text-2xl sm:text-3xl mb-3">{e.title}</h3>
                  <p className="text-black/60 leading-relaxed mb-5 max-w-2xl">{e.desc}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-black/60 mb-6">
                    <span className="flex items-center gap-2"><Calendar size={15} className="text-[#E63946]" />{fmt(e.date)}</span>
                    <span className="flex items-center gap-2"><Clock size={15} className="text-[#E63946]" />{e.time}{e.end_time ? ` – ${e.end_time}` : ""}</span>
                    <span className="flex items-center gap-2"><MapPin size={15} className="text-[#E63946]" />{e.location}</span>
                    {hasPaidTickets(e) && Number(e.price) > 0 && <span className="flex items-center gap-2 font-medium text-[#E63946]"><Ticket size={15} />From ${Math.min(Number(e.price) || Infinity, ...((e.ticket_tiers || []).map((t) => Number(t.price)).filter((p) => p > 0)))} Admission</span>}
                    {hasPaidTickets(e) && e.tickets_left !== undefined && e.tickets_left !== null && e.tickets_left !== "" && (
                      <span className={`flex items-center gap-2 font-medium ${Number(e.tickets_left) <= 0 ? "text-black/40" : Number(e.tickets_left) <= 50 ? "text-[#E63946]" : "text-black/60"}`} data-testid={`tickets-left-${i}`}>
                        {Number(e.tickets_left) <= 0 ? "Sold Out" : `${e.tickets_left} tickets left`}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {isSoldOut(e) ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-black/10 text-black/50 px-7 py-3.5 text-sm font-medium" data-testid={`event-soldout-${i}`}>Sold Out</span>
                    ) : e.sales_paused ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-black/10 text-black/50 px-7 py-3.5 text-sm font-medium">Sales Paused</span>
                    ) : hasPaidTickets(e) ? (
                      <Btn onClick={() => openTickets(e)} testid={`event-tickets-${i}`}><Ticket size={15} /> Get Tickets</Btn>
                    ) : (
                      <Btn onClick={() => openTickets(e)} testid={`event-register-${i}`}><Ticket size={15} /> Register Free</Btn>
                    )}
                    <Btn href={gcalLink(e)} variant="outline" testid={`event-calendar-${i}`}><CalendarPlus size={15} /> Add to Calendar</Btn>
                    <button onClick={() => share(e)} data-testid={`event-share-${i}`} className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-3.5 text-sm hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"><Share2 size={15} /> Share</button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {ev && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4 overflow-y-auto" onClick={() => setEv(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md my-8" onClick={(x) => x.stopPropagation()} data-testid="ticket-modal">
            <div className="flex items-start justify-between p-6 border-b border-black/10">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#E63946] mb-1">{hasPaidTickets(ev) ? "Get Tickets" : "Free Registration"}</p>
                <h3 className="font-display font-bold text-xl leading-tight">{ev.title}</h3>
                <p className="text-sm text-black/50 mt-1">{fmt(ev.date)}{ev.time ? ` · ${ev.time}` : ""}</p>
              </div>
              <button onClick={() => setEv(null)} className="grid place-items-center w-9 h-9 rounded-full hover:bg-black/5 shrink-0"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              {hasTiers && (
                <div>
                  <span className="block text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/60 mb-2">Ticket Type</span>
                  <div className="space-y-2">
                    {tiers.map((t) => {
                      const open = tierOpen(t);
                      return (
                        <button key={t.id} disabled={!open} onClick={() => setTierId(t.id)} data-testid={`tier-option-${t.id}`}
                          className={`w-full flex justify-between items-center border rounded-xl px-4 py-3 text-sm transition-colors ${tierId === t.id ? "border-[#E63946] bg-[#E63946]/5" : "border-black/15"} ${!open ? "opacity-40 cursor-not-allowed" : "hover:border-[#E63946]"}`}>
                          <span className="font-medium">{t.name}{!open ? " — unavailable" : ""}</span>
                          <span className="font-bold text-[#E63946]">{Number(t.price) > 0 ? `$${Number(t.price).toFixed(2)}` : "Free"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <Field label="Full Name" name="name" required value={tform.name} onChange={(e) => setTform({ ...tform, name: e.target.value })} />
              <Field label="Email" name="email" type="email" required value={tform.email} onChange={(e) => setTform({ ...tform, email: e.target.value })} />
              <Field label="Phone" name="phone" value={tform.phone} onChange={(e) => setTform({ ...tform, phone: e.target.value })} />
              <div>
                <span className="block text-xs uppercase tracking-[0.18em] text-[#0A0A0A]/60 mb-2">Quantity</span>
                <div className="flex items-center gap-4">
                  <button onClick={() => setTform({ ...tform, quantity: Math.max(1, Number(tform.quantity) - 1) })} className="grid place-items-center w-10 h-10 rounded-full border border-black/15 text-lg">−</button>
                  <span className="font-display font-bold text-2xl w-8 text-center" data-testid="ticket-qty">{tform.quantity}</span>
                  <button onClick={() => setTform({ ...tform, quantity: Math.min(Number(ev.max_per_order) || ev.tickets_left || 99, Number(tform.quantity) + 1) })} className="grid place-items-center w-10 h-10 rounded-full border border-black/15 text-lg">+</button>
                </div>
              </div>
              {hasPaidTickets(ev) && (
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
                    <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="ticket-promo-input"
                      className="w-full border border-black/15 rounded-full pl-8 pr-3 py-2.5 text-sm uppercase focus:border-[#E63946] outline-none" />
                  </div>
                  <button onClick={() => setAppliedPromo(promo)} data-testid="ticket-promo-apply"
                    className="rounded-full border border-black/15 px-4 py-2.5 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors">Apply</button>
                </div>
              )}
              {quote?.error && <p className="text-sm text-[#E63946]">{quote.error}</p>}
              {quote && !quote.error && (
                <div className="space-y-1.5 bg-[#FAFAFA] border border-black/10 rounded-xl p-4" data-testid="ticket-breakdown">
                  {row("Subtotal", `$${quote.subtotal.toFixed(2)}`)}
                  {quote.discount > 0 && row(`Discount${quote.promo_code ? ` (${quote.promo_code})` : ""}`, `-$${quote.discount.toFixed(2)}`, { red: true })}
                  {quote.service_fee > 0 && row(quote.service_fee_label || "Service Fee", `$${quote.service_fee.toFixed(2)}`)}
                  {quote.tax > 0 && row("Sales Tax", `$${quote.tax.toFixed(2)}`)}
                  {row("Total", `$${quote.total.toFixed(2)}`, { bold: true })}
                </div>
              )}
              {quote && !quote.error && quote.free ? (
                <button onClick={() => buyTickets("free")} disabled={!!buying} data-testid="ticket-register-free"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-4 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
                  {buying ? <Loader2 className="animate-spin" size={16} /> : <><Ticket size={17} /> Complete Free Registration</>}
                </button>
              ) : (
                <>
                  <button onClick={() => buyTickets("stripe")} disabled={!!buying || !quote || quote.error} data-testid="ticket-checkout-stripe"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-4 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
                    {buying === "stripe" ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : <><CreditCard size={17} /> Pay with Card</>}
                  </button>
                  <div className="flex items-center gap-3 text-xs text-black/30">
                    <span className="h-px bg-black/10 flex-1" /> or <span className="h-px bg-black/10 flex-1" />
                  </div>
                  <button onClick={() => buyTickets("paypal")} disabled={!!buying || !quote || quote.error} data-testid="ticket-checkout-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white py-4 font-medium hover:bg-[#001c4d] transition-colors disabled:opacity-60">
                    {buying === "paypal" ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : <>Checkout with <span className="font-bold italic">PayPal</span></>}
                  </button>
                </>
              )}
              {ev.refund_policy && <p className="text-xs text-black/40"><span className="font-medium">Refund policy:</span> {ev.refund_policy}</p>}
              <p className="text-center text-xs text-black/40">You'll receive a confirmation email with your ticket QR code. Card payments include Google Pay &amp; Apple Pay.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
